import { PropsWithChildren, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { BackgroundPattern } from '@/components/BackgroundPattern'
import { Close } from '@/components/icons/Close'
import { CloseSmallIcon } from '@/components/icons/CloseSmallIcon'
import { cn } from '@/utils/tailwind'

interface ModalAction {
  label: ReactNode
  onClick: () => void
  variant?: 'primary' | 'outline'
  disabled?: boolean
}

interface ModalHeader {
  icon: ReactNode
  title: string
}

interface BaseModalProps {
  isOpen?: boolean
  className?: string
  title?: string
  actions?: ModalAction[]
  dataTestId?: string
}

export type ModalProps =
  | (BaseModalProps & {
      fullScreen?: false
      header?: ModalHeader
      onClose?: () => void
    })
  | (BaseModalProps & {
      fullScreen: true
      header: ModalHeader
      onClose: () => void
    })

export const Modal = ({
  isOpen = true,
  className,
  title,
  header,
  onClose,
  actions,
  dataTestId,
  fullScreen,
  children
}: PropsWithChildren<ModalProps>) => {
  if (!isOpen) return null

  const displayTitle = title ?? header?.title

  if (fullScreen) {
    return createPortal(
      <div className="bg-primary-light fixed top-0 left-0 z-30 flex h-full w-full items-center justify-center sm:p-4">
        <div
          className={cn('bg-gray-background flex h-full w-full max-w-160 flex-col overflow-clip sm:rounded-lg sm:shadow-lg', className)}
          data-testid={dataTestId}
        >
          <div className="flex flex-row justify-between px-6 py-6">
            <div className="flex flex-row items-center gap-4">
              {header.icon}
              <p className="text-primary text-lg font-bold">{header.title}</p>
            </div>

            <button type="button" onClick={onClose} data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}>
              <Close customClassName="text-primary" />
            </button>
          </div>

          <BackgroundPattern className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-1 pb-4">{children}</BackgroundPattern>
        </div>
      </div>,
      document.body
    )
  }

  return (
    <div
      className={cn('animate-fade-in fixed top-0 left-0 z-20 flex h-full w-full items-center justify-center bg-[#071E38]/52 p-4', className)}
      data-testid={dataTestId}
      onClick={onClose}
    >
      <div className="flex w-full max-w-sm flex-col gap-2 rounded-2xl bg-white p-8" onClick={(event) => event.stopPropagation()}>
        {(displayTitle || onClose || header) && (
          <div className="flex flex-col gap-8">
            {onClose && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="flex h-6 w-6 items-center justify-center text-gray-500"
                  onClick={onClose}
                  aria-label="Close"
                  data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
                >
                  <CloseSmallIcon />
                </button>
              </div>
            )}
            {header?.icon}
            {displayTitle && <h2 className="text-center leading-tight font-bold">{displayTitle}</h2>}
          </div>
        )}

        {children}

        {actions && actions.length > 0 && (
          <div className="flex flex-col gap-4 pt-6">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'border-primary grow rounded-full px-2 py-3 font-bold uppercase',
                  action.variant === 'outline' ? 'text-primary' : 'bg-primary text-white',
                  action.disabled && 'opacity-50'
                )}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
